import { FORM_NAMES, TYPE_MEMBER } from 'core/constants'
import { IS_OPEN_MODAL, SNACKBAR_MESSAGE, STORE_ROOT_PROPS } from 'core/store/global'
import {
  getAccessToken,
  getConfig,
  getMemberId,
  getMemberType,
  getPartnerId,
  getUA,
} from 'core/singleton'

import { BRIDGE } from 'core/constants/bridge'
import { IFormInput } from 'components/Modal/FormModal'
import { useCallback, useEffect } from 'react'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import { useTranslation } from 'react-i18next'
import UserAPI from 'core/axios/userApi/user.api'
import { formatYMDHMS } from 'core/util/date'
import { getExecutorForPartner } from 'core/util/partner'
import { requestBridge } from 'core/util/default'
import { CONFIRM_ALARM } from 'core/store/alarm'
import { ConfirmType } from 'core/store/alarm/types'
import { NotificationParam } from '@types'

/**
 * 라이브 편성표의 알림 모달 관리용 Hook
 * 전체 상태를 변동시키는 이벤트는 모두 dispatch 이벤트로 관리 (모듈화의 상태도 관리 가능)
 */
export default function useHandleAlarm() {
  const setSnackbarMessage = useSetRecoilState(SNACKBAR_MESSAGE)
  const setIsOpenModal = useSetRecoilState(IS_OPEN_MODAL)
  const [confirmAlarmList, setConfirmAlarmList] = useRecoilState(CONFIRM_ALARM)
  const root = useRecoilValue(STORE_ROOT_PROPS)
  const memberId = getMemberId()
  const accessToken = getAccessToken()
  const partnerId = getPartnerId()
  const config = getConfig()

  const { guestLogin, alarm, redirection } = getConfig()
  const { t } = useTranslation()

  const handleSubscribeAlarm = useCallback(
    async ({ broadcastId, inputValue, ScheduleItem }) => {
      const formattedStartDate = formatYMDHMS(ScheduleItem?.programmingStartDt || '')
      const formattedEndDate = formatYMDHMS(ScheduleItem?.programmingEndDt || '')

      return await UserAPI.subscribeAlarm({
        broadcastUniqueId: broadcastId,
        partnerUniqueId: partnerId,
        partnerMemberUniqueId: memberId ?? '',
        programmingStartDate: formattedStartDate,
        programmingEndDate: formattedEndDate,
        notificationBeforeTime: 0,
        isPartnerMember: Boolean(!config?.guestLogin?.use),
        partnerMemberName: inputValue ? inputValue[FORM_NAMES.NAME] : undefined,
        partnerMemberPhoneNumber: inputValue ? inputValue[FORM_NAMES.NUMBER].toString() : undefined,
        isUseAlarm: getExecutorForPartner(partnerId)?.isUseAlarm ?? false,
      })
    },
    [memberId, partnerId, config?.guestLogin?.use],
  )

  const handleDeleteAlarm = useCallback(
    async ({ broadcastId }) => {
      return await UserAPI.deleteAlarm({
        broadcastUniqueId: broadcastId,
        partnerUniqueId: partnerId,
        partnerMemberUniqueId: memberId ?? '',
      })
    },
    [memberId, partnerId],
  )
  /**
   * @name handleBridgeAlarm
   * @description 알람 신청, 알람 취소 브릿지 세팅용 별도 함수
   */
  const handleBridgeAlarm = useCallback(
    async ({
      isConfirmAlarm,
      broadcastId,
      inputValue,
      ScheduleItem,
    }: {
      isConfirmAlarm: boolean
      broadcastId: string
      inputValue?: IFormInput
      ScheduleItem?: BroadcastItem | ScheduleItem
    }) => {
      if (
        root?.bridge ||
        (window[BRIDGE.CANCEL_NOTIFICATION] && window[BRIDGE.APPLY_NOTIFICATION])
      ) {
        const params: NotificationParam = {
          memberId: memberId ?? '', // 고객 ID
          programmingStartDt: ScheduleItem?.programmingStartDt
            ? ScheduleItem?.programmingStartDt
            : '', // 방송 예정시간 ex(UTC"2022-02-02T06:34:00.000Z")
          programmingEndDt: ScheduleItem?.programmingEndDt ? ScheduleItem?.programmingEndDt : '', // 방송 종료시간 ex(UTC"2022-02-02T06:34:00.000Z")
          broadcastId, // 방송ID
          accessToken: accessToken ?? '', // JWT TOKEN
        }

        /** API 전부 호출이후 브릿지 실행 필요 */
        if (root?.bridge || (window[BRIDGE.APPLY_NOTIFICATION] && !isConfirmAlarm)) {
          await handleSubscribeAlarm({ broadcastId, inputValue, ScheduleItem })
          requestBridge(BRIDGE.APPLY_NOTIFICATION, params)
          return true
        }
        if (root?.bridge || (window[BRIDGE.CANCEL_NOTIFICATION] && isConfirmAlarm)) {
          await handleDeleteAlarm({ broadcastId, inputValue, ScheduleItem })
          requestBridge(BRIDGE.CANCEL_NOTIFICATION, params)
          return true
        }
        return false
      }
    },
    [accessToken, handleDeleteAlarm, handleSubscribeAlarm, memberId, root?.bridge],
  )
  /**
   * @name 알림기능
   */

  const handleClickLogin = useCallback(() => {
    if (root?.bridge || window[BRIDGE.LOGIN]) {
      requestBridge(BRIDGE.LOGIN)
      return
    }
    const { isMobile } = getUA()
    const partnerLogin = redirection.login[isMobile ? 'mobile' : 'pc']
    if (partnerLogin) {
      if (redirection.login.history === 'replace') {
        window.location.replace(partnerLogin)
      } else {
        window.location.href = partnerLogin
      }
    }
  }, [redirection, root?.bridge])

  const handleSubmitAlarm = useCallback(
    async ({
      broadcastId,
      inputValue,
      ScheduleItem,
    }: {
      broadcastId: string
      inputValue?: IFormInput
      ScheduleItem?: BroadcastItem | ScheduleItem
    }) => {
      try {
        if (window[BRIDGE.APPLY_NOTIFICATION]) return
        const isConfirmAlarm = confirmAlarmList?.some(
          ({ broadcastId: confirmBroadcastId }) => broadcastId === confirmBroadcastId,
        )
        const res = await handleSubscribeAlarm({ broadcastId, inputValue, ScheduleItem })

        if (broadcastId && !isConfirmAlarm && res?.data.isCreate) {
          dispatchEvent(window[BRIDGE.APPLY_CHECK_NOTIFICATION]({ broadcastId }))
        }
        setSnackbarMessage(t('broadcast_notification_confirm'))
      } catch (e) {
        console.error(e)
      }
    },
    [confirmAlarmList, handleSubscribeAlarm, setSnackbarMessage, t],
  )

  const handleClickAlarm = useCallback(
    async ({
      broadcastId,
      ScheduleItem,
    }: {
      broadcastId: string
      ScheduleItem?: ScheduleItem | BroadcastItem
    }) => {
      const memberType = getMemberType()
      const isConfirmAlarm = confirmAlarmList?.some(
        ({ broadcastId: confirmBroadcastId }) => broadcastId === confirmBroadcastId,
      )
      if (guestLogin?.use && memberType === TYPE_MEMBER.NON_MEMBER) {
        if (broadcastId && isConfirmAlarm && !window[BRIDGE.CANCEL_NOTIFICATION]) {
          const res = await handleDeleteAlarm({ broadcastId })
          if (broadcastId && isConfirmAlarm && res.code) {
            dispatchEvent(window[BRIDGE.CANCEL_CHECK_NOTIFICATION]({ broadcastId }))
            setSnackbarMessage(t('broadcast_notification_cancel'))
          }
          return
        }

        const modalData = {
          type: 'form' as const,
          title: t('broadcast_notification_receive'),
          message: t('broadcast_notification_guide'),
          termsUrl: alarm?.agreementUrl,
          onSubmit: (inputValue?: IFormInput) => {
            handleSubmitAlarm({ broadcastId, inputValue, ScheduleItem })
          },
        }
        setIsOpenModal(modalData)
        return
      }
      if (!guestLogin?.use && memberType === TYPE_MEMBER.NON_MEMBER) {
        const modalData = {
          message: t('login_question'),
          onConfirm: handleClickLogin,
        }
        setIsOpenModal(modalData)
      } else if (memberType === TYPE_MEMBER.MEMBER) {
        if (await handleBridgeAlarm({ isConfirmAlarm, broadcastId, ScheduleItem })) {
          return
        }
        if (broadcastId && isConfirmAlarm) {
          if (window[BRIDGE.CANCEL_NOTIFICATION]) return
          const res = await handleDeleteAlarm({ broadcastId })

          if (broadcastId && isConfirmAlarm && res.code) {
            dispatchEvent(window[BRIDGE.CANCEL_CHECK_NOTIFICATION]({ broadcastId }))
          }
          setSnackbarMessage(t('broadcast_notification_cancel'))
          return
        }
        if (await handleBridgeAlarm({ isConfirmAlarm, broadcastId, ScheduleItem })) {
          return
        }
        const modalData = {
          type: 'confirm' as const,
          title: t('broadcast_notification_receive'),
          message: t('broadcast_notification_guide'),
          termsUrl: alarm?.agreementUrl,
          onConfirm: () => {
            handleSubmitAlarm({ broadcastId, ScheduleItem })
          },
        }
        setIsOpenModal(modalData)
      }
    },
    [
      confirmAlarmList,
      guestLogin?.use,
      t,
      alarm?.agreementUrl,
      setIsOpenModal,
      handleSubmitAlarm,
      handleClickLogin,
      handleBridgeAlarm,
      handleDeleteAlarm,
      setSnackbarMessage,
    ],
  )
  useEffect(() => {
    window[BRIDGE.APPLY_CHECK_NOTIFICATION] = (broadcast: ConfirmType) =>
      new CustomEvent(BRIDGE.APPLY_CHECK_NOTIFICATION, { detail: broadcast })

    window[BRIDGE.CANCEL_CHECK_NOTIFICATION] = (broadcast: ConfirmType) =>
      new CustomEvent(BRIDGE.CANCEL_CHECK_NOTIFICATION, { detail: broadcast })

    const handleApplyCheck = (e: CustomEvent) => {
      if (!e.detail) return
      const broadcast = e.detail
      setConfirmAlarmList((prev) => [...prev, broadcast])
    }

    const handleCancelCheck = (e: CustomEvent) => {
      if (!e.detail) return
      const { broadcastId } = e.detail
      const canceledAlarmList = confirmAlarmList.filter(
        ({ broadcastId: confirmBroadcastId }) => broadcastId !== confirmBroadcastId,
      )
      setConfirmAlarmList(canceledAlarmList)
    }

    window.addEventListener(BRIDGE.APPLY_CHECK_NOTIFICATION, handleApplyCheck)

    window.addEventListener(BRIDGE.CANCEL_CHECK_NOTIFICATION, handleCancelCheck)

    return () => {
      window.removeEventListener([BRIDGE.APPLY_CHECK_NOTIFICATION], handleApplyCheck)
      window.removeEventListener([BRIDGE.CANCEL_CHECK_NOTIFICATION], handleCancelCheck)
    }
  }, [confirmAlarmList, setConfirmAlarmList])

  return {
    handleSubmitAlarm,
    handleClickLogin,
    handleClickAlarm,
  }
}
